import React from 'react';
import { Box, Card, Skeleton, alpha } from '@mui/material';

const ShockDashboardSkeleton = () => {
  return (
    <Box sx={{ width: '100%' }}>
      {/* Page Header - Compact */}
      <Box sx={{ mb: 3 }}>
        <Skeleton 
          variant="text" 
          width={240} 
          height={40}
          animation="wave"
          sx={{ mb: 0.5 }}
        />
        <Skeleton 
          variant="text" 
          width={360} 
          height={24}
          animation="wave"
        />
      </Box>

      {/* Real-time Metrics Card */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            bgcolor: alpha('#0288d1', 0.04),
            px: { xs: 2, md: 3 },
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton 
                variant="rounded" 
                width={40} 
                height={40}
                animation="wave"
                sx={{ borderRadius: 2 }}
              />
              <Box>
                <Skeleton 
                  variant="text" 
                  width={180} 
                  height={28}
                  animation="wave"
                  sx={{ mb: 0.5 }}
                />
                <Skeleton 
                  variant="text" 
                  width={240} 
                  height={20}
                  animation="wave"
                />
              </Box>
            </Box>
            <Skeleton 
              variant="rounded" 
              width={140} 
              height={28}
              animation="wave"
              sx={{ borderRadius: 3 }}
            />
          </Box>
        </Box>

        {/* Summary Stats - 4 Cards in a Row */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            p: { xs: 2, md: 3 }
          }}
        >
          {[1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                p: 2.5,
                borderRadius: 2,
                bgcolor: alpha('#667eea', 0.04),
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Skeleton 
                  variant="rounded" 
                  width={32} 
                  height={32}
                  animation="wave"
                  sx={{ borderRadius: 1.5 }}
                />
                <Skeleton 
                  variant="text" 
                  width={80} 
                  height={20}
                  animation="wave"
                />
              </Box>
              <Skeleton 
                variant="text" 
                width="60%" 
                height={36}
                animation="wave"
                sx={{ mb: 0.5 }}
              />
              <Skeleton 
                variant="text" 
                width="80%" 
                height={18}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      </Card>

      {/* Shock Chart Card */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3
        }}
      >
        {/* Chart Header */}
        <Box 
          sx={{ 
            bgcolor: alpha('#667eea', 0.04),
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton 
              variant="rounded" 
              width={40} 
              height={40}
              animation="wave"
              sx={{ borderRadius: 2 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton 
                variant="text" 
                width={220} 
                height={26}
                animation="wave"
                sx={{ mb: 0.5 }}
              />
              <Skeleton 
                variant="text" 
                width={320} 
                height={20}
                animation="wave"
              />
            </Box>
          </Box>
        </Box>

        {/* Chart Area - Compact */}
        <Box sx={{ p: 3 }}>
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={280}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Card>

      {/* Top 10 Shocks Card */}
      <Card 
        elevation={0}
        sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        {/* Section Header */}
        <Box 
          sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 3,
            py: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton 
              variant="rounded" 
              width={40} 
              height={40}
              animation="wave"
              sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)' }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton 
                variant="text" 
                width={200} 
                height={26}
                animation="wave"
                sx={{ bgcolor: 'rgba(255,255,255,0.3)' }}
              />
            </Box>
          </Box>
        </Box>

        {/* Shock Items */}
        <Box sx={{ p: 3 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                mb: i === 10 ? 0 : 1.5,
                borderRadius: 2,
                bgcolor: alpha('#667eea', 0.02),
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {/* Rank */}
              <Skeleton 
                variant="circular" 
                width={36} 
                height={36}
                animation="wave"
              />

              {/* Indicator Info */}
              <Box sx={{ flex: 1 }}>
                <Skeleton 
                  variant="text" 
                  width="40%" 
                  height={22}
                  animation="wave"
                  sx={{ mb: 0.5 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton 
                    variant="rounded" 
                    width={60} 
                    height={20}
                    animation="wave"
                    sx={{ borderRadius: 3 }}
                  />
                  <Skeleton 
                    variant="rounded" 
                    width={80} 
                    height={20}
                    animation="wave"
                    sx={{ borderRadius: 3 }}
                  />
                </Box>
              </Box>

              {/* Value */}
              <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                <Skeleton 
                  variant="text" 
                  width={60} 
                  height={28}
                  animation="wave"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              {/* Weight */}
              <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                <Skeleton 
                  variant="text" 
                  width={50} 
                  height={24}
                  animation="wave"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              {/* Articles */}
              <Skeleton 
                variant="rounded" 
                width={70} 
                height={24}
                animation="wave"
                sx={{ borderRadius: 3 }}
              />

              {/* Status */}
              <Skeleton 
                variant="rounded" 
                width={80} 
                height={24}
                animation="wave"
                sx={{ borderRadius: 3 }}
              />
            </Box>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default ShockDashboardSkeleton;
